import { MantineColorsTuple, rgba } from "@mantine/core";
import { MantineTheme } from "../mantine";

export const darkColorsTuple: MantineColorsTuple = [
    "#F4F4F5",
    "#CBCBCB",
    "#979CA9",
    "#767B84",
    "#5C6067",
    "#4A4D53",
    "#474A50",
    "#3C3F45",
    "#323439",
    "#242529",
];

// Figma primitive dark/dark-1-5: between dark-1 and dark-2, so dimmed text passes 4.5:1 on raised surfaces.
// Kept out of darkColorsTuple, whose ten shades Mantine indexes.
const DARK_1_5_COLOR = "#AAB0BA";

export const grayColorsTuple: MantineColorsTuple = [
    "#F8F9FA",
    "#F1F3F5",
    "#E9ECEF",
    "#DEE2E6",
    "#CED4DA",
    "#ADB5BD",
    "#868E96",
    "#495057",
    "#343A40",
    "#212529",
];
export const blueColorsTuple: MantineColorsTuple = [
    "#E7F5FF",
    "#D0EBFF",
    "#A5D8FF",
    "#74C0FC",
    "#4DABF7",
    "#339AF0",
    "#228BE6",
    "#1C7ED6",
    "#1971C2",
    "#1864AB",
];

type SchemeColor = { light: string; dark: string };

/**
 * Every theme-aware colour token, with its light and dark value side by side, so a token can't be added to one scheme
 * and forgotten in the other.
 */
const getColorTokens = (theme: MantineTheme) =>
    ({
        // Surface
        "--surface-subtle-default": { light: rgba(theme.black, 0.1), dark: rgba(theme.white, 0.12) },
        "--surface-subtle-hover": { light: rgba(theme.black, 0.04), dark: rgba(theme.white, 0.05) },
        "--surface-page-color": { light: theme.colors.gray[1], dark: theme.colors.dark[9] },
        "--mantine-color-body": { light: "var(--surface-page-color)", dark: "var(--surface-page-color)" },
        "--surface-container-color": { light: theme.white, dark: theme.colors.dark[8] },
        "--surface-default-color": { light: theme.white, dark: theme.colors.dark[7] },
        "--surface-overlay-color": { light: theme.white, dark: theme.colors.dark[7] },
        "--surface-solid-1-color": { light: theme.white, dark: theme.black },
        "--surface-solid-2-color": { light: theme.black, dark: theme.white },
        "--surface-permanent-1-color": { light: theme.white, dark: theme.white },
        "--surface-permanent-2-color": { light: theme.black, dark: theme.black },

        // Surface/gray
        "--surface-gray-solid": { light: theme.colors.gray[6], dark: theme.colors.dark[2] },
        "--surface-gray-solid-hover": { light: theme.colors.gray[7], dark: theme.colors.gray[6] },
        "--surface-gray-transparent": {
            light: rgba(theme.colors.gray[6], 0.1),
            dark: rgba(theme.colors.gray[6], 0.15),
        },
        "--surface-gray-transparent-hover": {
            light: rgba(theme.colors.gray[6], 0.15),
            dark: rgba(theme.colors.gray[6], 0.2),
        },
        "--surface-gray-fade": { light: theme.colors.gray[1], dark: theme.colors.dark[6] },

        // Surface/Violet
        "--surface-violet-solid": { light: theme.colors.violet[5], dark: theme.colors.violet[3] },

        // Surface/success
        "--surface-success-solid": { light: theme.colors.teal[6], dark: theme.colors.teal[6] },
        "--surface-success-solid-hover": { light: theme.colors.teal[7], dark: theme.colors.teal[7] },
        "--surface-success-transparent": {
            light: rgba(theme.colors.teal[6], 0.1),
            dark: rgba(theme.colors.teal[3], 0.15),
        },
        "--surface-success-transparent-hover": {
            light: rgba(theme.colors.teal[6], 0.15),
            dark: rgba(theme.colors.teal[3], 0.2),
        },
        "--surface-success-prominent": { light: theme.colors.teal[9], dark: theme.colors.teal[3] },
        "--surface-success-fade": { light: theme.colors.teal[1], dark: theme.colors.teal[9] },

        // Surface/primary
        "--surface-primary-solid": { light: theme.colors.blue[6], dark: theme.colors.blue[6] },
        "--surface-primary-solid-hover": { light: theme.colors.blue[7], dark: theme.colors.blue[7] },
        "--surface-primary-transparent": {
            light: rgba(theme.colors.blue[6], 0.1),
            dark: rgba(theme.colors.blue[3], 0.15),
        },
        "--surface-primary-transparent-hover": {
            light: rgba(theme.colors.blue[6], 0.15),
            dark: rgba(theme.colors.blue[3], 0.2),
        },
        "--surface-primary-prominent": { light: theme.colors.blue[9], dark: theme.colors.blue[3] },
        "--surface-primary-fade": { light: theme.colors.blue[1], dark: theme.colors.blue[9] },

        // Surface/warning
        "--surface-warning-solid-color": { light: theme.colors.yellow[6], dark: theme.colors.yellow[6] },
        "--surface-warning-solid-hover-color": { light: theme.colors.yellow[7], dark: theme.colors.yellow[7] },
        "--surface-warning-transparent-color": {
            light: rgba(theme.colors.yellow[6], 0.1),
            dark: rgba(theme.colors.yellow[7], 0.15),
        },
        "--surface-warning-transparent-hover-color": {
            light: rgba(theme.colors.yellow[6], 0.15),
            dark: rgba(theme.colors.yellow[7], 0.2),
        },
        "--surface-warning-prominent-color": { light: theme.colors.yellow[9], dark: theme.colors.yellow[4] },
        "--surface-warning-fade-color": { light: theme.colors.yellow[1], dark: theme.colors.yellow[9] },

        // Surface/error
        "--surface-error-solid": { light: theme.colors.red[6], dark: theme.colors.red[6] },
        "--surface-error-solid-hover": { light: theme.colors.red[7], dark: theme.colors.red[7] },
        "--surface-error-transparent": { light: rgba(theme.colors.red[6], 0.1), dark: rgba(theme.colors.red[3], 0.15) },
        "--surface-error-transparent-hover": {
            light: rgba(theme.colors.red[6], 0.15),
            dark: rgba(theme.colors.red[3], 0.2),
        },
        "--surface-error-prominent": { light: theme.colors.red[9], dark: theme.colors.red[3] },
        "--surface-error-fade": { light: theme.colors.red[1], dark: theme.colors.red[9] },

        "--surface-primary-solid-color": {
            light: "var(--mantine-primary-color-6)",
            dark: "var(--mantine-primary-color-6)",
        },
        "--surface-primary-prominent-color": { light: theme.colors.blue[9], dark: theme.colors.blue[3] },
        "--surface-primary-transparent-color": {
            light: rgba(theme.colors.blue[6], 0.1),
            dark: rgba(theme.colors.blue[3], 0.15),
        },
        "--surface-primary-transparent-hover-color": {
            light: rgba(theme.colors.blue[6], 0.15),
            dark: rgba(theme.colors.blue[3], 0.2),
        },

        "--surface-violet-prominent-color": { light: theme.colors.violet[9], dark: theme.colors.violet[3] },
        "--surface-violet-transparent-color": {
            light: rgba(theme.colors.violet[6], 0.1),
            dark: rgba(theme.colors.violet[3], 0.15),
        },
        "--surface-success-prominent-color": { light: theme.colors.teal[9], dark: theme.colors.teal[3] },
        "--surface-success-transparent-color": {
            light: rgba(theme.colors.teal[6], 0.1),
            dark: rgba(theme.colors.teal[3], 0.15),
        },
        "--surface-error-prominent-color": { light: theme.colors.red[9], dark: theme.colors.red[3] },
        "--surface-error-transparent-color": {
            light: rgba(theme.colors.red[6], 0.1),
            dark: rgba(theme.colors.red[3], 0.15),
        },
        "--surface-cyan-prominent-color": { light: theme.colors.cyan[9], dark: theme.colors.cyan[3] },
        "--surface-cyan-transparent-color": {
            light: rgba(theme.colors.cyan[6], 0.1),
            dark: rgba(theme.colors.cyan[3], 0.15),
        },

        // Surface/pink
        "--surface-pink-solid-color": { light: theme.colors.pink[5], dark: theme.colors.pink[3] },
        "--surface-pink-transparent-color": {
            light: rgba(theme.colors.pink[6], 0.1),
            dark: rgba(theme.colors.pink[3], 0.15),
        },
        "--surface-pink-prominent-color": { light: theme.colors.pink[9], dark: theme.colors.pink[3] },
        "--surface-pink-fade-color": { light: theme.colors.pink[1], dark: theme.colors.pink[9] },

        "--surface-gray-transparent-color": {
            light: rgba(theme.colors.gray[6], 0.1),
            dark: rgba(theme.colors.gray[6], 0.15),
        },
        "--surface-gray-transparent-hover-color": {
            light: rgba(theme.colors.blue[6], 0.15),
            dark: rgba(theme.colors.blue[6], 0.2),
        },

        // Border
        "--border-transparent-default-1-color": { light: rgba(theme.black, 0.1), dark: rgba(theme.white, 0.15) },
        "--border-transparent-default-2-color": { light: rgba(theme.white, 0.15), dark: rgba(theme.black, 0.1) },
        "--border-highlight-default-color": { light: rgba(theme.white, 0), dark: theme.colors.dark[6] },
        "--border-default-color": { light: theme.colors.gray[3], dark: theme.colors.dark[4] },
        "--border-focus-color": { light: theme.colors.blue[6], dark: theme.colors.blue[4] },
        "--border-red-dark-15-color": { light: rgba(theme.colors.red[6], 0.15), dark: rgba(theme.colors.red[6], 0.15) },

        "--mantine-color-title-1": { light: theme.white, dark: "#000000" },
        "--mantine-color-title-2": { light: theme.black, dark: "#FFFFFF" },
        "--mantine-color-default": { light: theme.white, dark: theme.colors.dark[6] },
        "--mantine-color-default-hover": { light: rgba(theme.black, 0.04), dark: "rgba(255, 255, 255, 0.05)" },
        "--mantine-color-surface-container": { light: theme.white, dark: theme.colors.dark[8] },
        "--mantine-color-surface-container-border": { light: rgba(theme.white, 0), dark: theme.colors.dark[6] },

        "--mantine-color-primary-filled": { light: theme.colors.blue[6], dark: theme.colors.blue[6] },
        "--mantine-color-primary-filled-hover": { light: theme.colors.blue[7], dark: theme.colors.blue[7] },
        "--mantine-color-primary-light": {
            light: rgba(theme.colors.blue[6], 0.1),
            dark: rgba(theme.colors.blue[5], 0.1),
        },
        "--mantine-color-primary-light-hover": {
            light: rgba(theme.colors.blue[6], 0.15),
            dark: rgba(theme.colors.blue[5], 0.2),
        },
        "--mantine-color-primary-fade-1": { light: theme.colors.blue[9], dark: theme.colors.blue[3] },
        "--mantine-color-primary-fade-2": { light: theme.colors.blue[1], dark: theme.colors.blue[9] },

        "--mantine-color-transparent-default": { light: rgba(theme.black, 0.1), dark: "rgba(255, 255, 255, 0.13)" },
        "--mantine-color-disabled-default": { light: theme.colors.gray[1], dark: "rgba(0, 0, 0, 0)" },

        "--mantine-color-success-filled": { light: theme.colors.teal[6], dark: theme.colors.teal[6] },
        "--mantine-color-success-filled-hover": { light: theme.colors.teal[7], dark: theme.colors.teal[7] },
        "--mantine-color-success-light": {
            light: rgba(theme.colors.teal[6], 0.1),
            dark: rgba(theme.colors.teal[5], 0.15),
        },
        "--mantine-color-success-light-hover": {
            light: rgba(theme.colors.teal[6], 0.15),
            dark: rgba(theme.colors.teal[5], 0.2),
        },
        "--mantine-color-success-fade-1": { light: theme.colors.teal[9], dark: theme.colors.teal[3] },
        "--mantine-color-success-fade-2": { light: theme.colors.teal[1], dark: theme.colors.teal[9] },

        "--mantine-color-warning-filled": { light: theme.colors.yellow[6], dark: theme.colors.yellow[5] },
        "--mantine-color-warning-filled-hover": { light: theme.colors.yellow[7], dark: theme.colors.yellow[6] },
        "--mantine-color-warning-light": {
            light: rgba(theme.colors.yellow[6], 0.1),
            dark: rgba(theme.colors.yellow[5], 0.15),
        },
        "--mantine-color-warning-light-hover": {
            light: rgba(theme.colors.yellow[6], 0.15),
            dark: rgba(theme.colors.yellow[5], 0.2),
        },
        "--mantine-color-warning-fade-1": { light: theme.colors.yellow[9], dark: theme.colors.yellow[3] },
        "--mantine-color-warning-fade-2": { light: theme.colors.yellow[1], dark: theme.colors.yellow[9] },

        "--mantine-color-error-filled": { light: theme.colors.red[6], dark: theme.colors.red[6] },
        "--mantine-color-error-filled-hover": { light: theme.colors.red[7], dark: theme.colors.red[7] },
        "--mantine-color-error-light": { light: rgba(theme.colors.red[6], 0.1), dark: rgba(theme.colors.red[5], 0.15) },
        "--mantine-color-error-light-hover": {
            light: rgba(theme.colors.red[6], 0.15),
            dark: rgba(theme.colors.red[5], 0.2),
        },
        "--mantine-color-error-fade-1": { light: theme.colors.red[9], dark: theme.colors.red[3] },
        "--mantine-color-error-fade-2": { light: theme.colors.red[1], dark: theme.colors.red[9] },

        /* Text */
        "--mantine-color-text-title-1": { light: "#FFFFFF", dark: "#000000" },
        "--mantine-color-text-title-2": { light: "#000000", dark: "#FFFFFF" },
        "--mantine-color-text-default": { light: theme.colors.gray[9], dark: theme.colors.dark[0] },
        "--mantine-color-text-dimmed": { light: theme.colors.gray[6], dark: "var(--mantine-color-dimmed)" },
        // Empty state copy: dimmed grey in light, full-strength text in dark where dimmed is too faint.
        "--mantine-color-text-empty-state": { light: theme.colors.gray[6], dark: theme.colors.dark[0] },
        "--mantine-color-text-link": { light: theme.colors.blue[6], dark: "var(--mantine-color-anchor)" },
        "--mantine-color-text-placeholder": { light: theme.colors.gray[5], dark: "var(--mantine-color-placeholder)" },

        /* Text / Primary */
        "--mantine-color-text-primary-default": {
            light: "var(--mantine-primary-color-6)",
            dark: "var(--mantine-primary-color-3)",
        },
        "--mantine-color-text-primary-prominent": { light: theme.colors.blue[9], dark: theme.colors.blue[3] },
        "--mantine-color-text-primary-fade-1": { light: theme.colors.blue[9], dark: theme.colors.blue[3] },
        "--mantine-color-text-primary-fade-2": { light: theme.colors.blue[1], dark: theme.colors.blue[9] },

        /* Text / Disabled */
        "--mantine-color-text-disabled": { light: theme.colors.gray[5], dark: theme.colors.dark[4] },

        /* Text / Success */
        "--mantine-color-text-success-default": { light: theme.colors.teal[6], dark: theme.colors.teal[5] },
        "--mantine-color-text-success-prominent": { light: theme.colors.teal[9], dark: theme.colors.teal[3] },
        "--mantine-color-text-success-fade-1": { light: theme.colors.teal[9], dark: theme.colors.teal[3] },
        "--mantine-color-text-success-fade-2": { light: theme.colors.teal[1], dark: theme.colors.teal[9] },

        /* Text / Warning */
        "--mantine-color-text-warning-default": { light: theme.colors.yellow[6], dark: theme.colors.yellow[5] },
        "--mantine-color-text-warning-prominent": { light: theme.colors.yellow[9], dark: theme.colors.yellow[4] },
        "--mantine-color-text-warning-fade": { light: theme.colors.yellow[1], dark: theme.colors.yellow[9] },
        "--mantine-color-text-warning-fade-1": { light: theme.colors.yellow[9], dark: theme.colors.yellow[3] },
        "--mantine-color-text-warning-fade-2": { light: theme.colors.yellow[1], dark: theme.colors.yellow[9] },

        /* Text / Error */
        "--mantine-color-text-error-default": { light: theme.colors.red[6], dark: "var(--mantine-color-error)" },
        "--mantine-color-text-error-prominent": { light: theme.colors.red[9], dark: theme.colors.red[3] },
        "--mantine-color-text-error-fade": { light: theme.colors.red[1], dark: theme.colors.red[9] },
        "--mantine-color-text-error-fade-1": { light: theme.colors.red[9], dark: theme.colors.red[3] },
        "--mantine-color-text-error-fade-2": { light: theme.colors.red[1], dark: theme.colors.red[9] },
        "--mantine-color-text-error-disabled": { light: theme.colors.red[4], dark: "rgba(224, 49, 49, 0.5)" },

        /* Text / On solid fills */
        "--mantine-color-text-on-success": { light: theme.white, dark: theme.colors.dark[9] },

        /* Text / Indigo */
        "--mantine-color-text-indigo-fade-1": { light: theme.colors.indigo[9], dark: theme.colors.indigo[3] },
        "--mantine-color-text-indigo-fade-2": { light: theme.colors.indigo[1], dark: theme.colors.indigo[9] },

        /* Text / Grape */
        "--mantine-color-text-grape-fade-1": { light: theme.colors.grape[9], dark: theme.colors.grape[3] },
        "--mantine-color-text-grape-fade-2": { light: theme.colors.grape[1], dark: theme.colors.grape[9] },

        /* Text / Cyan */
        "--mantine-color-text-cyan-fade-1": { light: theme.colors.cyan[9], dark: theme.colors.cyan[1] },
        "--mantine-color-text-cyan-fade-2": { light: theme.colors.cyan[1], dark: theme.colors.cyan[9] },

        /* Text / Orange */
        "--mantine-color-text-orange-fade-1": { light: theme.colors.orange[9], dark: theme.colors.orange[3] },
        "--mantine-color-text-orange-fade-2": { light: theme.colors.orange[1], dark: theme.colors.orange[9] },

        /* Text / Violet */
        "--mantine-color-text-violet-default": { light: theme.colors.violet[5], dark: theme.colors.violet[3] },
        "--mantine-color-text-violet-fade-1": { light: theme.colors.violet[9], dark: theme.colors.violet[3] },
        "--mantine-color-text-violet-fade-2": { light: theme.colors.violet[1], dark: theme.colors.violet[9] },

        /* Text / Pink */
        "--mantine-color-text-pink-fade-1": { light: theme.colors.pink[9], dark: theme.colors.pink[3] },
        "--mantine-color-text-pink-fade-2": { light: theme.colors.pink[1], dark: theme.colors.pink[9] },
        // Text on inverted surfaces (Neutral/90: tooltips, toasts, hover cards), which flip between schemes
        "--mantine-color-text-inverse-dimmed": { light: theme.colors.gray[5], dark: theme.colors.gray[7] },
        "--mantine-color-text-inverse-link": {
            light: "var(--mantine-primary-color-4)",
            dark: "var(--mantine-primary-color-8)",
        },
        "--mantine-color-text-inverse-success": { light: theme.colors.teal[5], dark: theme.colors.teal[9] },
        "--mantine-color-text-inverse-error": { light: theme.colors.red[4], dark: theme.colors.red[9] },
        "--mantine-color-text-inverse-warning": { light: theme.colors.yellow[6], dark: theme.colors.yellow[9] },

        /* Border */
        "--mantine-color-border-default": { light: theme.colors.gray[3], dark: theme.colors.dark[4] },

        "--mantine-color-border-primary-default": { light: theme.colors.blue[6], dark: theme.colors.blue[6] },

        "--mantine-color-border-transparent-default-1": {
            light: "rgba(0, 0, 0, 0.1)",
            dark: "rgba(255, 255, 255, 0.13)",
        },
        "--mantine-color-border-transparent-default-2": {
            light: "rgba(255, 255, 255, 0.13)",
            dark: "rgba(0, 0, 0, 0.1)",
        },

        "--mantine-color-border-highlight-default-1": { light: "#FFFFFF", dark: "rgba(0, 0, 0, 0)" },
        "--mantine-color-border-highlight-default-2": { light: "rgba(255, 255, 255, 0)", dark: theme.colors.dark[7] },

        "--mantine-color-border-warning-default": { light: theme.colors.yellow[6], dark: theme.colors.yellow[5] },
        "--mantine-color-border-success-default": { light: theme.colors.teal[6], dark: theme.colors.teal[5] },
        "--mantine-color-border-error-default": { light: theme.colors.red[6], dark: theme.colors.red[5] },
        "--mantine-color-border-gray-default": { light: theme.colors.gray[6], dark: theme.colors.gray[6] },

        // Menu Item
        "--menu-item-section-color": { light: theme.colors.gray[6], dark: theme.colors.dark[2] },
        "--menu-item-section-color-hover": { light: theme.colors.gray[9], dark: theme.colors.dark[0] },

        // Input
        "--input-section-right-color": { light: theme.colors.gray[9], dark: theme.colors.dark[0] },

        // Charts. Dimmed and Others are the same in light, but dimmed must recede in dark while Others stays readable.
        "--chart-other-color": { light: "var(--mantine-color-neutral-4)", dark: theme.colors.dark[2] },
        "--chart-other-hover-color": { light: "var(--mantine-color-neutral-5)", dark: theme.colors.dark[1] },
        "--chart-dimmed-color": { light: "var(--mantine-color-neutral-4)", dark: theme.colors.dark[4] },
        // Light: the legacy donut colours ($flat-silver, $flat-clouds-2) are kept as is so light mode doesn't change.
        "--chart-default-color": { light: "#BDC3C7", dark: theme.colors.dark[3] },
        "--chart-empty-color": { light: "#DDE4E6", dark: theme.colors.dark[6] },
        // Dark: the darker fades keep white cell text at 5:1 or more across the whole opacity scale.
        "--chart-heat-pass-color": { light: "var(--surface-success-solid)", dark: "var(--surface-success-fade)" },
        "--chart-heat-fail-color": { light: "var(--surface-error-solid)", dark: "var(--surface-error-fade)" },
        "--chart-interaction-chat-color": {
            light: "var(--surface-primary-solid)",
            // The other interaction types use fixed shade-5 colours. Surface/Primary/solid is blue-6 in dark, so Chat
            // keeps blue-5 to match them.
            dark: theme.colors.blue[5],
        },

        // Neutral colors
        "--mantine-color-neutral-0": { light: theme.colors.gray[0], dark: theme.colors.dark[9] },
        "--mantine-color-neutral-1": { light: theme.colors.gray[1], dark: theme.colors.dark[8] },
        "--mantine-color-neutral-2": { light: theme.colors.gray[2], dark: theme.colors.dark[8] },
        "--mantine-color-neutral-3": { light: theme.colors.gray[3], dark: theme.colors.dark[2] },
        "--mantine-color-neutral-4": { light: theme.colors.gray[4], dark: theme.colors.dark[4] },
        "--mantine-color-neutral-5": { light: theme.colors.gray[5], dark: theme.colors.dark[3] },
        "--mantine-color-neutral-6": { light: theme.colors.gray[6], dark: theme.colors.dark[2] },
        "--mantine-color-neutral-7": { light: theme.colors.gray[7], dark: theme.colors.dark[1] },
        "--mantine-color-neutral-8": { light: theme.colors.gray[8], dark: theme.colors.dark[1] },
        "--mantine-color-neutral-9": { light: theme.colors.gray[9], dark: theme.colors.dark[0] },
    }) satisfies Record<string, SchemeColor>;

/**
 * Mantine's own variables that only change in dark mode. Light keeps Mantine's defaults, so these have no light value.
 */
const getDarkOnlyOverrides = (theme: MantineTheme): Record<string, string> => ({
    // Mantine generates gray-filled from shade 8, which disappears into dark surfaces; follow Surface/Gray/solid.
    "--mantine-color-gray-filled": "var(--surface-gray-solid)",
    "--mantine-color-gray-filled-hover": "var(--surface-gray-solid-hover)",

    // Mantine's own variables carry the design values so Mantine components (c="dimmed", input
    // placeholders, Anchor) and ours render the same colour.
    "--mantine-color-dimmed": DARK_1_5_COLOR,
    "--mantine-color-placeholder": theme.colors.dark[2],
    "--mantine-color-anchor": theme.colors.blue[3],
    "--mantine-color-blue-filled": "var(--mantine-color-primary-filled)",
    "--mantine-color-blue-filled-hover": "var(--mantine-color-primary-filled-hover)",
    "--mantine-color-error": theme.colors.red[4],
});

const pickScheme = (tokens: Record<string, SchemeColor>, scheme: keyof SchemeColor) =>
    Object.fromEntries(Object.entries(tokens).map(([name, values]) => [name, values[scheme]]));

export const lightColors = (theme: MantineTheme) => pickScheme(getColorTokens(theme), "light");

export const darkColors = (theme: MantineTheme) => ({
    ...pickScheme(getColorTokens(theme), "dark"),
    ...getDarkOnlyOverrides(theme),
});
